/**
 * A short explanation attached to a number, a bar or a heading. Hover shows it, so does
 * keyboard focus, and Escape closes it; the trigger is a real button so a screen reader
 * reaches the text through `aria-describedby` rather than a title attribute nothing
 * reads reliably.
 *
 * It is kept out of the layout until it is shown, and measured when it is: a panel
 * anchored to the left of a trigger near the right edge used to widen the page by its
 * own width, so every record route scrolled sideways with nothing visible out there.
 */
import { useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";

export function Tip({ children, what }: { children: ComponentChildren; what: string }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<"left" | "right">("left");
  const panel = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const el = panel.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.right > window.innerWidth - 8) setSide("right");
    else if (r.left < 8) setSide("left");
  }, [open]);

  return (
    <span class="tip-wrap" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button" class="tip-trigger" aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); (e.currentTarget as HTMLButtonElement).blur(); } }}
      >
        {children}
      </button>
      {open && <span ref={panel} id={id} role="tooltip" class={`tip ${side}`}>{what}</span>}
    </span>
  );
}
